/**
 * @module  public/js/core/net/NetHealthService.js
 * @class   NetHealthService
 * @summary Monitors browser connectivity to classify health as GOOD, DEGRADED or OFFLINE.
 *
 * @description
 * NetHealthService keeps an eye on the browser's network conditions so the app can react to
 * connectivity issues quickly. It watches the `navigator.onLine` flag, listens for online/offline
 * events, samples the Network Information API when available, and periodically performs a fetch
 * probe using an `AbortController` timeout. Consumers can start and stop monitoring, read the
 * current status, and subscribe to status changes.
 *
 * ## Public API
 * – `start()` — begin monitoring and start the probe loop.
 * – `stop()` — halt monitoring, remove listeners, and cancel inflight probes.
 * – `getStatus()` — return the most recent connectivity classification.
 * – `onStatusChange(callback)` — register a listener that is notified on status transitions.
 */
export const STATUSES = Object.freeze({
  GOOD: 'GOOD',
  DEGRADED: 'DEGRADED',
  OFFLINE: 'OFFLINE',
});

const DEFAULT_PROBE_URL = 'https://www.gstatic.com/generate_204';
const DEFAULT_PROBE_INTERVAL = 30000;
const DEFAULT_PROBE_TIMEOUT = 3000;

const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

export class NetHealthService {
  constructor({ probeUrl, probeInterval, probeTimeout } = {}) {
    this._status = STATUSES.GOOD;
    this._listeners = new Set();
    this._started = false;

    this._probeUrl = probeUrl || DEFAULT_PROBE_URL;
    this._probeInterval =
      typeof probeInterval === 'number'
        ? probeInterval
        : DEFAULT_PROBE_INTERVAL;
    this._probeTimeout =
      typeof probeTimeout === 'number' ? probeTimeout : DEFAULT_PROBE_TIMEOUT;

    this._intervalId = null;
    this._lastProbeSucceeded = null;
    this._probeController = null;
    this._pendingProbePromise = null;

    this._handleOnline = this._handleOnline.bind(this);
    this._handleOffline = this._handleOffline.bind(this);
    this._handleConnectionChange = this._handleConnectionChange.bind(this);
  }

  start() {
    if (this._started) {
      return;
    }

    this._started = true;
    this._attachEventListeners();
    this._evaluateStatus();
    this._runProbe();
    this._scheduleProbe();
  }

  stop() {
    if (!this._started) {
      return;
    }

    this._started = false;
    this._detachEventListeners();

    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }

    if (this._probeController) {
      this._probeController.abort();
      this._probeController = null;
    }

    this._pendingProbePromise = null;
    this._lastProbeSucceeded = null;
  }

  getStatus() {
    return this._status;
  }

  onStatusChange(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError('onStatusChange expects a function callback');
    }

    this._listeners.add(callback);

    return () => {
      this._listeners.delete(callback);
    };
  }

  setProbeUrl(url) {
    if (typeof url !== 'string' || !url) {
      throw new TypeError('Probe URL must be a non-empty string');
    }

    this._probeUrl = url;
  }

  setProbeInterval(intervalMs) {
    if (
      typeof intervalMs !== 'number' ||
      !Number.isFinite(intervalMs) ||
      intervalMs <= 0
    ) {
      throw new TypeError('Probe interval must be a positive number');
    }

    this._probeInterval = intervalMs;

    if (this._started) {
      this._scheduleProbe(true);
    }
  }

  setProbeTimeout(timeoutMs) {
    if (
      typeof timeoutMs !== 'number' ||
      !Number.isFinite(timeoutMs) ||
      timeoutMs <= 0
    ) {
      throw new TypeError('Probe timeout must be a positive number');
    }

    this._probeTimeout = timeoutMs;
  }

  _getWindow() {
    if (typeof window !== 'undefined') {
      return window;
    }

    if (typeof globalThis !== 'undefined' && globalThis.window) {
      return globalThis.window;
    }

    return null;
  }

  _getNavigator() {
    if (typeof navigator !== 'undefined') {
      return navigator;
    }

    if (typeof globalThis !== 'undefined' && globalThis.navigator) {
      return globalThis.navigator;
    }

    const win = this._getWindow();
    return win && win.navigator ? win.navigator : null;
  }

  _getConnection() {
    const nav = this._getNavigator();
    if (!nav) {
      return null;
    }

    return nav.connection || nav.mozConnection || nav.webkitConnection || null;
  }

  _attachEventListeners() {
    const win = this._getWindow();
    if (win && typeof win.addEventListener === 'function') {
      win.addEventListener('online', this._handleOnline);
      win.addEventListener('offline', this._handleOffline);
    }

    const connection = this._getConnection();
    if (connection) {
      if (typeof connection.addEventListener === 'function') {
        connection.addEventListener('change', this._handleConnectionChange);
      } else if ('onchange' in connection) {
        connection.onchange = this._handleConnectionChange;
      }
    }
  }

  _detachEventListeners() {
    const win = this._getWindow();
    if (win && typeof win.removeEventListener === 'function') {
      win.removeEventListener('online', this._handleOnline);
      win.removeEventListener('offline', this._handleOffline);
    }

    const connection = this._getConnection();
    if (connection) {
      if (typeof connection.removeEventListener === 'function') {
        connection.removeEventListener('change', this._handleConnectionChange);
      } else if (
        'onchange' in connection &&
        connection.onchange === this._handleConnectionChange
      ) {
        connection.onchange = null;
      }
    }
  }

  _handleOnline() {
    this._evaluateStatus();
    this._runProbe();
  }

  _handleOffline() {
    this._lastProbeSucceeded = false;
    this._evaluateStatus();
  }

  _handleConnectionChange() {
    this._evaluateStatus();
  }

  _scheduleProbe(reschedule = false) {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }

    if (!this._started) {
      return;
    }

    this._intervalId = setInterval(() => {
      this._runProbe();
    }, this._probeInterval);

    if (reschedule) {
      this._runProbe();
    }
  }

  async _runProbe() {
    if (!this._started) {
      return;
    }

    const nav = this._getNavigator();
    if (!nav || nav.onLine === false) {
      this._lastProbeSucceeded = false;
      this._evaluateStatus();
      return;
    }

    if (this._pendingProbePromise) {
      return this._pendingProbePromise;
    }

    const controller = new AbortController();
    this._probeController = controller;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, this._probeTimeout);

    const fetchImpl = typeof fetch === 'function' ? fetch : null;

    if (!fetchImpl) {
      clearTimeout(timeoutId);
      this._lastProbeSucceeded = null;
      this._probeController = null;
      this._evaluateStatus();
      return;
    }

    this._pendingProbePromise = fetchImpl(this._probeUrl, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(() => {
        clearTimeout(timeoutId);
        this._lastProbeSucceeded = true;
        this._probeController = null;
        this._evaluateStatus();
      })
      .catch(() => {
        clearTimeout(timeoutId);
        this._lastProbeSucceeded = false;
        this._probeController = null;
        this._evaluateStatus();
      })
      .finally(() => {
        this._pendingProbePromise = null;
      });

    return this._pendingProbePromise;
  }

  _isConnectionDegraded() {
    const connection = this._getConnection();
    if (!connection) {
      return false;
    }

    if (typeof connection.saveData === 'boolean' && connection.saveData) {
      return true;
    }

    if (
      typeof connection.effectiveType === 'string' &&
      SLOW_EFFECTIVE_TYPES.has(connection.effectiveType)
    ) {
      return true;
    }

    if (
      typeof connection.downlink === 'number' &&
      connection.downlink > 0 &&
      connection.downlink < 1
    ) {
      return true;
    }

    return false;
  }

  _evaluateStatus() {
    const nav = this._getNavigator();
    const isOnline = !!(nav && nav.onLine !== false);

    if (!isOnline) {
      this._updateStatus(STATUSES.OFFLINE);
      return;
    }

    if (this._lastProbeSucceeded === false) {
      this._updateStatus(STATUSES.DEGRADED);
      return;
    }

    if (this._isConnectionDegraded()) {
      this._updateStatus(STATUSES.DEGRADED);
      return;
    }

    this._updateStatus(STATUSES.GOOD);
  }

  _updateStatus(nextStatus) {
    if (this._status === nextStatus) {
      return;
    }

    this._status = nextStatus;
    for (const listener of Array.from(this._listeners)) {
      try {
        listener(this._status);
      } catch (err) {
        setTimeout(() => {
          throw err;
        }, 0);
      }
    }
  }
}

const netHealthServiceSingleton = new NetHealthService();
export default netHealthServiceSingleton;
